<template>
  <a-modal :open="open" :footer="null" :width="520" destroy-on-close @cancel="close">
    <template #title>
      <div class="modal-title">내 계정 보안</div>
    </template>

    <!-- 창 안 여백·닫기 줄은 전역 .security-modal(ant-design.scss), 「2차 인증」 창과 같은 규칙 -->
    <div class="modal-content security-modal">
      <a-spin :spinning="loading">
        <!-- 상태 머리: 켜짐/꺼짐 + 필수 계정 + 「2차 인증 사용」 스위치(A17, D-132) -->
        <div class="status-head">
          <span class="status-name">2차 인증</span>
          <a-tag :color="sw.on ? 'green' : 'default'">{{ sw.on ? "켜짐" : "꺼짐" }}</a-tag>
          <a-tag v-if="requiredAccount" color="gold">필수 계정</a-tag>
          <!-- 수단 0개면 켤 것이 없어 스위치 대신 아래 안내 줄만. 필수 계정은 켜짐으로 잠근다 -->
          <label v-if="sw.kind !== 'NONE'" class="switch-wrap">
            <span>2차 인증 사용</span>
            <a-switch
              :checked="sw.on"
              :disabled="sw.kind === 'LOCKED'"
              :loading="toggling"
              @change="toggle"
            />
          </label>
          <p class="status-desc">{{ STATUS_DESC[sw.kind] || sw.reason }}</p>
          <!-- 결과는 스위치 바로 아래 짧게 -->
          <p v-if="result" class="status-result" :class="result.type" role="status">
            {{ result.text }}
          </p>
        </div>

        <!-- 수단마다 한 줄: 이름 · 가린 값 · 등록 / 변경·삭제 (A14~A16). 등록·변경 입력은 그 행 아래에 붙는다 -->
        <div class="factor-list">
          <div v-for="m in METHODS" :key="m" class="factor-item">
            <div class="factor-row">
              <span class="factor-label">{{ METHOD_LABEL[m] }}</span>
              <span class="factor-value" :class="{ empty: !factors.targets[m] }">
                {{ factors.targets[m] || "없음" }}
              </span>
              <div class="factor-actions">
                <a-button size="small" :disabled="!!editing" @click="startEdit(m)">
                  {{ factors.targets[m] ? "변경" : "등록" }}
                </a-button>
                <a-button
                  v-if="factors.targets[m]"
                  size="small"
                  danger
                  :disabled="!!editing || lastEnforced"
                  :loading="busy === m"
                  @click="remove(m)"
                >
                  삭제
                </a-button>
              </div>
            </div>

            <!-- 등록·변경: 새 번호·이메일로 받은 인증번호 확인 -->
            <div v-if="editing === m" class="edit-box">
              <otp-step
                :key="editing"
                :methods="[editing]"
                enroll
                :send="sendCode"
                :verify="verifyCode"
                :submit-text="factors.targets[editing] ? '변경' : '등록'"
              >
                <a-button block class="edit-cancel" @click="editing = null">취소</a-button>
              </otp-step>
            </div>
          </div>
        </div>
        <p v-if="lastEnforced" class="help">{{ TWO_FACTOR_ENFORCED_MESSAGE }}</p>

        <p v-if="error" class="error">{{ error }}</p>
      </a-spin>

      <div class="modal-footer">
        <a-button @click="close">닫기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from "ant-design-vue";
import apiInstance from "@/axios";
import { confirmDialog } from "@/utils/utils";
import {
  METHOD_LABEL,
  TWO_FACTOR_ENFORCED_MESSAGE,
  errorMessage,
  factorsOf,
  isLastEnforcedFactor,
  isStepUpCancelled,
  markStepUpCancelled,
  saveSessionTokens,
  stepUpPurposeOf,
  twoFactorSwitch,
} from "@/utils/mfa";
import { requestStepUp } from "@/utils/stepUp";
import OtpStep from "./OtpStep.vue";

const METHODS = ["ALIMTALK_OTP", "FALLBACK_EMAIL_OTP"];

// 스위치 상태별 설명 줄. LOCKED 는 이유(sw.reason)를 쓴다
const STATUS_DESC = {
  NONE: "휴대폰이나 이메일을 등록하면 2차 인증이 켜집니다.",
  ON: "휴대폰이나 이메일이 등록돼 있어 로그인할 때 인증번호를 입력합니다.",
  OFF: "인증번호 없이 로그인합니다. 등록한 휴대폰·이메일은 그대로 있습니다.",
};

/**
 * 운영자 본인 「내 계정 보안」(REQ-ACC-012, design.md §6-1 A14~A16). 상단 바에서 연다.
 * 휴대폰·이메일 수단을 등록·변경·삭제한다. 운영자는 필수 계정이라 마지막 수단은 지울 수 없다(D-93).
 * 이미 수단이 있으면 서버가 유효한 증표를 요구한다(M*) — `/auth/2fa/*` 는 axios 인터셉터가 step-up 을 띄우지 않으므로
 * 여기서 MFA_REQUIRED 를 받으면 step-up 창을 직접 띄우고 한 번 다시 보낸다.
 * 등록·삭제 응답의 새 access·refresh 토큰(mfaReq 다시 계산)과 증표를 바로 저장한다(감사 D6).
 * SESSION_NOT_ALLOWED(공용 비밀번호 세션)는 서버 문구 그대로, 재시도 없음.
 * 「2차 인증 사용」 스위치(A17 PUT /auth/2fa/enabled, D-132): 끄기는 확인창 → (증표 없으면 본인 확인) → 저장, 켜기는 바로.
 * 필수 여부는 role 이 아니라 서버 requiredBy 로만 정한다(D-133).
 */
export default {
  name: "MySecurityModal",
  components: { OtpStep },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return {
      METHODS,
      METHOD_LABEL,
      TWO_FACTOR_ENFORCED_MESSAGE,
      STATUS_DESC,
      mfa: {},
      loading: false,
      editing: null,
      busy: "",
      error: "",
      toggling: false,
      result: null,
    };
  },
  computed: {
    factors() {
      return factorsOf(this.mfa);
    },
    lastEnforced() {
      return isLastEnforcedFactor(this.mfa);
    },
    sw() {
      return twoFactorSwitch(this.mfa);
    },
    // 본인이 수단을 등록해 생긴 필수(SELF)는 켜짐만으로 충분해 필수 태그를 달지 않는다
    requiredAccount() {
      return !!this.mfa.required && this.mfa.requiredBy !== "SELF";
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.editing = null;
        this.error = "";
        this.result = null;
        this.load();
      },
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await apiInstance.get("/auth/profile", { showGlobalLoading: false });
        this.mfa = (data && data.mfa) || {};
      } catch (error) {
        console.error(error);
        this.error = errorMessage(error, "보안 상태를 불러오지 못했습니다.");
      } finally {
        this.loading = false;
      }
    },
    // M*: 증표가 없거나 만료면 step-up 창을 띄우고 통과하면 한 번 다시 보낸다
    async withStepUp(call) {
      try {
        return await call();
      } catch (error) {
        if (stepUpPurposeOf(error) !== "LOGIN") throw error;
        if (!(await requestStepUp("LOGIN"))) throw markStepUpCancelled(error);
        return call();
      }
    },
    startEdit(method) {
      this.error = "";
      this.editing = method;
    },
    async sendCode(method, target) {
      const { data } = await this.withStepUp(() =>
        apiInstance.post("/auth/2fa/factors/challenge", { method, target }, { showGlobalLoading: false })
      );
      return data;
    },
    async verifyCode(method, code) {
      const { data } = await this.withStepUp(() =>
        apiInstance.post("/auth/2fa/factors/verify", { method, code }, { showGlobalLoading: false })
      );
      this.applyResult(data);
      this.editing = null;
      message.success(`${METHOD_LABEL[method]} 수단을 저장했습니다.`);
    },
    async remove(method) {
      // 대상과 결과를 말하고 확인 버튼은 빨간 「삭제」(D7). 마지막 수단이면 2차 인증이 꺼진다
      // 가린 값을 문장 앞에 두어 확인창 좁은 폭에서 번호가 줄바꿈으로 갈라지지 않게 한다
      const last = this.factors.methods.length <= 1;
      const isPhone = method === "ALIMTALK_OTP";
      const ok = await confirmDialog(
        isPhone ? "휴대폰 삭제" : "이메일 삭제",
        `${this.factors.targets[method]} ${isPhone ? "휴대폰" : "이메일"}을 지웁니다. 이 ${
          isPhone ? "휴대폰으로는" : "이메일로는"
        } 더 이상 인증번호를 받을 수 없습니다.${last ? " 지우면 2차 인증이 꺼집니다." : ""}`,
        { confirmText: "삭제", danger: true }
      );
      if (!ok) return;
      this.busy = method;
      this.error = "";
      try {
        const { data } = await this.withStepUp(() =>
          apiInstance.delete(`/auth/2fa/factors/${method}`, { showGlobalLoading: false })
        );
        this.applyResult(data);
        message.success(`${METHOD_LABEL[method]} 수단을 지웠습니다.`);
      } catch (error) {
        console.error(error);
        if (!isStepUpCancelled(error)) {
          this.error = errorMessage(error, "지우지 못했습니다. 잠시 뒤 다시 시도하세요.");
        }
      } finally {
        this.busy = "";
      }
    },
    // 끄기는 확인 뒤, 켜기는 바로. 실패하면 스위치는 그대로(:checked 가 서버 상태)
    async toggle(enabled) {
      if (!enabled) {
        const ok = await confirmDialog(
          "2차 인증 끄기",
          "2차 인증을 끕니다. 로그인하거나 메시지를 보낼 때 인증번호를 묻지 않습니다. 등록한 휴대폰·이메일은 그대로 남아 언제든 다시 켤 수 있습니다.",
          { confirmText: "끄기", danger: true }
        );
        if (!ok) return;
      }
      this.toggling = true;
      this.result = null;
      try {
        const { data } = await this.withStepUp(() =>
          apiInstance.put("/auth/2fa/enabled", { enabled }, { showGlobalLoading: false })
        );
        this.applyResult(data);
        this.result = { type: "ok", text: enabled ? "2차 인증을 켰습니다." : "2차 인증을 껐습니다." };
      } catch (error) {
        console.error(error);
        if (isStepUpCancelled(error)) return;
        this.result = { type: "fail", text: errorMessage(error, "바꾸지 못했습니다. 잠시 뒤 다시 시도하세요.") };
        // 그 사이 회사 강제가 켜졌거나(403 정책) 다른 곳에서 수단을 지웠으면(400) 서버 상태로 다시 그린다
        this.load();
      } finally {
        this.toggling = false;
      }
    },
    // A15·A16·A17 응답 {mfa, mfaToken?, accessToken, refreshToken}. 지난 스위치 결과 줄은 지운다
    applyResult(data = {}) {
      saveSessionTokens(data);
      if (data.mfa) this.mfa = data.mfa;
      this.result = null;
    },
  },
};
</script>

<style scoped>
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

/* 상태 머리: 「2차 인증」 창 요약과 같은 회색 띠 */
.status-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 20px;
  border-radius: 12px;
  background: #f7f8f9;
}
.status-name {
  margin-right: 4px;
  font-size: 15px;
  font-weight: 700;
  color: #17191c;
}
.status-head :deep(.ant-tag) {
  margin: 0;
  font-size: 13px;
  line-height: 22px;
}
.switch-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 0 auto;
  font-size: 14px;
  font-weight: 600;
  color: #17191c;
}
/* 꺼짐 스위치가 앱 공통 연회색이라 회색 바탕과 구분이 안 된다(회사 강제 스위치와 같은 값, M15) */
.switch-wrap :deep(.ant-switch:not(.ant-switch-checked)) {
  background: #8d939a;
}
.status-desc,
.status-result {
  flex-basis: 100%;
  margin: 0;
  font-size: 13px;
  color: #4b5158;
}
/* 회색 바탕에서도 대비 4.5:1 이상(D8). 아래 .error(불러오기 오류 줄)와 겹치지 않는 이름 */
.status-result.ok {
  color: #237804;
}
.status-result.fail {
  color: #b23a3a;
}

/* 수단 목록 상자: 행마다 이름 · 가린 값 · 버튼 한 줄 */
.factor-list {
  margin-top: 16px;
  border: 1px solid #e8ebed;
  border-radius: 12px;
}
.factor-item + .factor-item {
  border-top: 1px solid #e8ebed;
}
.factor-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 10px 20px;
}
.factor-label {
  font-size: 14px;
  color: #6e747c;
}
.factor-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  color: #17191c;
}
.factor-value.empty {
  font-weight: 400;
  color: #5c6168;
}
.factor-actions {
  display: flex;
  gap: 6px;
}

/* 등록·변경 입력: 그 행 아래에 붙는 상자 */
.edit-box {
  margin: 0 20px 16px;
  padding: 16px;
  border-radius: 10px;
  background: #f7f8f9;
}
.edit-cancel {
  height: 40px;
  color: #2f3337;
}

.help {
  margin: 8px 0 0;
  font-size: 12px;
  color: #5c6168;
}
.error {
  margin: 8px 0 0;
  color: #c64b4b;
  font-size: 0.875rem;
}
</style>
